import { takeLatest, call, put, select } from "redux-saga/effects";
import YmsNotification from "utils/functions/notification";
import { translate } from "utils/intl/IntlProvider";
import { getError } from "utils/requests/api.errors";
import { Creators as CreatorsTags, Types } from "./../tags/tags-duck";
import * as Api from "./tags-api";

export const getFilterFromStateUserSession = (state) => state.UserSession;
export const getTagsFromState = (state) => state.Tags;

export const onExtractRegisterData = (registersData) =>
  registersData.items.map((item) => {
    return {
      id: item.id,
      tagNumber: item.tagNumber,
      vehiclePlate: item.vehiclePlate,
      locationDescription: item.locationDescription,
      createdAt: item.createdAt,
      inactivatedAt: item.inactivatedAt,
      userName: item.user,
      disabledEdit: item.status === 0,
    };
  });

export function* onTagsRegisterFetchAsync() {
  try {
    yield put(CreatorsTags.tagsShowLoadingTable());
    const tags = yield select(getTagsFromState);
    const resp = yield call(Api.getTagsRegister, {
      filter: tags.searchFilter,
      _size: tags.filter.itemsByPage,
      _page: tags.filter.pageIndex,
    });

    const tagsData = resp.data;
    const tagsItems = onExtractRegisterData(tagsData);

    yield put(CreatorsTags.tagsFetchRegister(tagsItems, tagsData.totalItems));
  } catch (error) {
    const requestErrorMessage = yield getError(error) ?? translate("tags_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(CreatorsTags.tagsHideLoadingTable());
  }
}

const onExtractTagData = (registerTag) => {
  return {
    id: registerTag.id,
    isTagActive: registerTag.status === 1,
    ievNumber: registerTag.iev,
    tagNumber: registerTag.tagNumber,
    vehiclePlate: registerTag.vehiclePlate,
    locationDescription: registerTag.locationDescription,
  };
};

export function* onTagsFetchByIdAsync(action) {
  try {
    yield put(CreatorsTags.tagsShowLoadingTable());

    const resp = yield call(Api.getTagById, { id: action.id });
    const data = onExtractTagData(resp.data);

    yield put(CreatorsTags.tagsDataTag(data));
  } catch (error) {
    yield put(CreatorsTags.tagsHideDrawer());
    const requestErrorMessage = yield getError(error) ?? translate("tag_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(CreatorsTags.tagsHideLoadingTable());
  }
}

export function* onTagsImportCsv(action) {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!userSessionState.isAdmin) {
    return;
  }
  try {
    yield put(CreatorsTags.tagsShowLoadingImportCsvModal());
    const file: File = action.data;

    const formData = new FormData();
    formData.append("file", file);

    const resp = yield call(Api.saveImportCsv, formData);

    const tagsData = resp.data;
    yield put(CreatorsTags.tagsUpdateResultCsvData(tagsData));
    const successMessage = yield translate("csv_import_tag_completed");

    yield YmsNotification({
      type: "success",
      description: successMessage,
    });
    yield onTagsRegisterFetchAsync();
  } catch (error) {
    const requestErrorMessage = yield getError(error) ?? translate("there_was_an_error_importing_the_tags");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(CreatorsTags.tagsHideLoadingImportCsvModal());
  }
}

export function* onUpdateTag(action) {
  try {
    yield put(CreatorsTags.tagsShowLoadingEditDrawer());

    const tagStateDefault = yield select(getTagsFromState);
    let tagsState = yield select(getTagsFromState);

    const {
      initialTagActiveState,
      editTag: { isTagActive },
    } = tagsState;

    const tagChangedToInactive = initialTagActiveState === true && isTagActive === false;

    if (tagChangedToInactive && !tagsState.tagHasInactiveUserAccepted) {
      yield put(CreatorsTags.tagsUpdateTagInactive(true));
      yield put(CreatorsTags.tagsHideLoadingEditDrawer());
      return;
    }
    const tag = {
      id: action.tag.id,
      status: action.tag.status === false || action.tag.status === 0 ? 0 : 1,
      vehiclePlate: action.tag.vehiclePlate,
      vehicleAlreadyHasTag: tagStateDefault.vehicleTagInfo.vehicleAlreadyHasTag,
    };

    const resp = yield call(Api.editTags, tag);
    const { vehicleAlreadyHasTag, vehiclePlate, tagNumber } = resp.data;

    tagsState = yield select(getTagsFromState);

    if (vehicleAlreadyHasTag && !tagsState.vehicleAlreadyHasTagUserAccepted) {
      yield put(
        CreatorsTags.tagsUpdateVehicleTagInfo({
          vehicleAlreadyHasTag: true,
          tagNumber: tagNumber,
          vehiclePlate: vehiclePlate,
        })
      );
      yield put(CreatorsTags.tagsHideLoadingEditDrawer());
      return;
    }

    const successMessage = yield translate("tag_updated_successfully");

    yield YmsNotification({
      type: "success",
      description: successMessage,
    });

    yield put(CreatorsTags.tagsHideDrawer());
    yield onTagsRegisterFetchAsync();
  } catch (error) {
    const requestErrorMessage = (yield getError(error)) ?? translate("tag_could_not_be_saved");

    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(CreatorsTags.tagsHideLoadingEditDrawer());
  }
}

export default function* TagsSagas() {
  yield takeLatest(Types.TAGS_REGISTER_FETCH_ASYNC, onTagsRegisterFetchAsync);
  yield takeLatest(Types.TAGS_FETCH_BY_ID_ASYNC, onTagsFetchByIdAsync);
  yield takeLatest(Types.TAGS_IMPORT_CSV_ASYNC, onTagsImportCsv);
  yield takeLatest(Types.TAGS_UPDATE_TAG_ASYNC, onUpdateTag);
}
